"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FieldReference = void 0;
const Expression_1 = require("./Expression");
const constant_1 = require("./../constant");
const FormulaParser_1 = require("../parser/FormulaParser");
const FormulaTokenizer_1 = require("../tokenizer/FormulaTokenizer");
const FormulaInterpreter_1 = require("../interpreter/FormulaInterpreter");
/**
 * Represents a reference to a field in a given object, allowing
 * for the execution of expressions and the interpretation of formulas.
 *
 * @template T - The type of the object containing the fields.
 * @template R - The type of the return value of the expression.
 * @param {string} fieldName - The name of the field to reference in the object.
 */
class FieldReference extends Expression_1.Expression {
    constructor(fieldName) {
        super();
        this.fieldName = fieldName;
    }
    /**
     * Executes the field reference on the given object.
     *
     * @param {T} obj - The object from which to extract the field value.
     * @returns {R} The value of the referenced field.
     * @throws {Error} If the field does not exist or is undefined in the object.
     */
    execute(obj) {
        if (obj != null && obj != undefined) {
            if (obj[this.fieldName] != undefined) {
                if (this.isFormulaRef())
                    return this.executeFormulaRef(obj);
                if (this.isCompiledExpression(obj))
                    return this.evaluateCompiledExpression(obj);
                return obj[this.fieldName];
            }
        }
        throw new Error(`The fieldName ${this.fieldName} does not exist or is undefined on object ${obj}`);
    }
    /**
     * Checks if the field name corresponds to a formula reference.
     *
     * @returns {boolean} True if the field is a formula reference, otherwise false.
     */
    isFormulaRef() {
        return constant_1.REGEX.formulaFieldName.test(this.fieldName);
    }
    /**
     * Checks if the field name corresponds to a Compiled Expression
     * @param obj {T} The object from which to extract the field value.
     * @returns {boolean}  True if the field is a formula reference, otherwise false.
     */
    isCompiledExpression(obj) {
        var _a;
        return ((_a = obj[this.fieldName]) === null || _a === void 0 ? void 0 : _a.type) === "CompiledExpression";
    }
    /**
     * Executes the formula reference and returns the result of the interpretation.
     *
     * @param {T} obj - The object from which to extract the formula.
     * @returns {R} The result of executing the formula.
     */
    executeFormulaRef(obj) {
        const fTokenizer = new FormulaTokenizer_1.FormulaTokenizer();
        const fParser = new FormulaParser_1.FormulaParser();
        const fInterpreter = new FormulaInterpreter_1.FormulaInterpreter();
        const astTree = fParser.execute(fTokenizer.execute(obj[this.fieldName]));
        return fInterpreter.execute(astTree, obj);
    }
    /**
     * Evaluate the compiled Expression
     * @param obj - The object from which to extract the formula.
     * @returns The result of evaluate the compiled Expression.
     */
    evaluateCompiledExpression(obj) {
        const compiledExpression = obj[this.fieldName];
        return compiledExpression.evaluate(obj);
    }
}
exports.FieldReference = FieldReference;
